import type { TContentBlock, TPullQuoteBlock } from "@/zapi-frontend/shared/types/contentBlocks";
import { splitFigureSpans } from "@/zapi-frontend/shared/lib/figureRefs";
import { useFigureRefs } from "./FigureRefs/FigureRefsProvider";
import { FigureTrigger } from "./FigureRefs/FigureTrigger";
import { memoType } from "./memoTypography";
import { cn } from "@/zapi-frontend/shared/lib/utils";

// A quote either carries its own line or points at the block it quotes. The pointer is read at
// draw time, so an edit to the quoted paragraph reaches the quote instead of leaving a stale copy.
export const quotedText = (block: TPullQuoteBlock, blocks: TContentBlock[]) => {
  if (block.text) return { text: block.text, refs: block.refs };
  const source = blocks.find((other) => other.kind === "prose" && other.id === block.from);
  return source?.kind === "prose" ? { text: source.text, refs: source.refs } : null;
};

export function PullQuoteBlock({ block, blocks = [] }: { block: TPullQuoteBlock; blocks?: TContentBlock[] }) {
  const figures = useFigureRefs();
  const quoted = quotedText(block, blocks);
  if (!quoted) return null;
  const parts = figures ? splitFigureSpans(quoted.text, quoted.refs, figures) : [{ text: quoted.text }];

  return (
    <figure className="flex items-center gap-4">
      <div className="w-0.5 shrink-0 self-stretch rounded-[2px] bg-innoveraZinc-200" />
      <div className={cn("flex min-w-0 flex-1 flex-col gap-2 py-1", memoType.quote)}>
        {block.label && <figcaption className="uppercase text-innoveraZinc-500">{block.label}</figcaption>}
        <blockquote className="font-medium italic text-innoveraZinc-900">
          {parts.map((part, i) =>
            part.key ? (
              <FigureTrigger key={i} figureKey={part.key}>
                {part.text}
              </FigureTrigger>
            ) : (
              <span key={i}>{part.text}</span>
            )
          )}
        </blockquote>
      </div>
    </figure>
  );
}
